import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { startOfDay } from "date-fns";
import { toast } from "sonner";
import { useConfirm } from "@omit/react-confirm-dialog";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { WidgetParamKeys } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { NudgeUsersModal } from "@/react/components/entityViews/checklist/components/editors/NudgeUsersModal";
import type { Checklist } from "@/react/components/entityViews/checklist/types";
import { fetchChecklistDetail } from "@/react/components/entityViews/checklist/api/checklist.service";
import {
  fetchChecklistItemStatusLogQuery,
  useDeleteChecklistMutation,
  useDuplicateChecklistMutation,
  useExportChecklistItemsMutation,
  useImportChecklistItemsMutation,
  usePatchChecklistCellMutation,
  usePatchChecklistMutation
} from "@/react/components/entityViews/checklist/api/checklist.api";
import type { ChecklistEntityPathType } from "@/react/components/entityViews/checklist/api/checklist.query-keys";
import { CheckgridsRail } from "./CheckgridsRail";
import { CheckgridsOverview } from "./CheckgridsOverview";
import { CheckgridsFilters } from "./CheckgridsFilters";
import { CheckgridDetail } from "./CheckgridDetail";
import type { CheckgridMenuAction } from "./CheckgridMenu";
import { useCheckgridsOverview } from "./hooks/useCheckgridsOverview";
import { useCheckgridDetail } from "./hooks/useCheckgridDetail";
import { useBoardUsers } from "@/react/hooks/useBoardUsers";
import { useDebounce } from "@/react/hooks/useDebounce";
import { usePersistedCheckgridFilters } from "./hooks/usePersistedCheckgridFilters";
import { boardDay } from "./utils/dayWindow";
import { historyFromStatusLog } from "./utils/apiAdapters";
import { boardListQueryFilters, countActiveFilters, filterGrids } from "./utils/filters";
import {
  checklistRecordFromOverview,
  entityTypeForSubjectType,
  hostEntityIdFromOverview,
  storeKeyForSubjectType,
  subjectTypeFromOverview
} from "./utils/hostEntity";
import type { CheckGridCellPatch, CheckGridOverviewItem, CheckGridSubjectType } from "./types";
import { UNASSIGNED_SUBJECT_ID } from "./types";

function readBoardId(selectedBoard: unknown): number | null {
  const board = selectedBoard as { id?: number; record?: { id?: number } } | null;
  return board?.id ?? board?.record?.id ?? null;
}

function readTimezone(selectedBoard: unknown): string | null {
  const board = selectedBoard as { timezone?: string; record?: { timezone?: string } } | null;
  return board?.timezone ?? board?.record?.timezone ?? null;
}

function CheckgridDetailSkeleton({ name }: { name: string }) {
  const cols = Array.from({ length: 7 }, (_, i) => i);
  const rows = Array.from({ length: 6 }, (_, i) => i);
  return (
    <div
      className="flex h-full min-w-0 flex-col overflow-hidden bg-background animate-in fade-in duration-150 motion-reduce:animate-none"
      aria-busy="true"
      aria-label={`Loading ${name}`}
    >
      <header className="border-b border-border px-8 py-5">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[20px] tracking-tight text-foreground">{name}</h1>
            <div className="mt-2.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
              <Skeleton className="h-8 w-52 max-w-full rounded-lg" />
              <Skeleton className="h-1.5 w-16 rounded-full" />
              <Skeleton className="h-3 w-14" />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Skeleton className="size-8 rounded-md" />
            <Skeleton className="size-8 rounded-md" />
          </div>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 px-8 py-6">
        <div className="flex w-[224px] shrink-0 flex-col" aria-hidden>
          <div className="shrink-0" style={{ height: 132 }} />
          <div className="flex flex-col gap-5 pt-2">
            {rows.map((row) => (
              <Skeleton key={row} className="h-3.5" style={{ width: `${72 - row * 4}%` }} />
            ))}
          </div>
        </div>
        <div className="min-w-0 flex-1 overflow-hidden" aria-hidden>
          <div className="mb-4 flex items-end gap-3" style={{ height: 132 }}>
            {cols.map((col) => (
              <Skeleton key={col} className="w-3 rounded-sm" style={{ height: `${56 + ((col * 13) % 40)}%` }} />
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {rows.map((row) => (
              <div key={row} className="flex gap-3">
                {cols.map((col) => (
                  <Skeleton key={col} className="h-11 w-[64px] shrink-0 rounded-md" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-sm text-destructive">{message}</p>
      <Button type="button" size="sm" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

export function CheckgridsView() {
  const { selectedBoard, project, user, entityManager, editorHandler } = useSwivuBryntumContext();
  const confirm = useConfirm();
  const boardId = readBoardId(selectedBoard);
  const timeZone = readTimezone(selectedBoard);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [day, setDay] = useState(() => startOfDay(new Date()));
  const [savingCell, setSavingCell] = useState(false);
  const [nudgeChecklist, setNudgeChecklist] = useState<Checklist | null>(null);

  const [filters, setFilters] = usePersistedCheckgridFilters(boardId);
  const debouncedQuery = useDebounce(filters.query, 500);
  // Clearing the box applies at once; typing waits. Rail and Right now share this text so they stay in step.
  const searchQuery = filters.query.trim() ? debouncedQuery : "";
  const { assignedTo, filterEntityType, createdWithinDays, search, recurrence } = boardListQueryFilters({ ...filters, query: searchQuery });
  const {
    grids: overviewGrids,
    progressGrids: overviewProgressGrids,
    totalCount,
    isLoading,
    isProgressLoading,
    error,
    progressError,
    refresh,
    patchSummary,
    patchOverview,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useCheckgridsOverview(boardId, false, { assignedTo, filterEntityType, createdWithinDays, search, recurrence });
  const patchChecklistMutation = usePatchChecklistMutation();
  const deleteChecklistMutation = useDeleteChecklistMutation();
  const duplicateChecklistMutation = useDuplicateChecklistMutation();
  const importChecklistItemsMutation = useImportChecklistItemsMutation();
  const exportChecklistItemsMutation = useExportChecklistItemsMutation();
  const patchChecklistCellMutation = usePatchChecklistCellMutation();
  const findHostEntity = useCallback(
    (subjectType: CheckGridSubjectType, entityId: number) => project?.getCrudStore?.(storeKeyForSubjectType(subjectType))?.getById?.(entityId) ?? null,
    [project]
  );
  // Rows without an API entity title fall back to the name of the task/client/resource they belong to.
  const withEntityNames = useCallback(
    (list: CheckGridOverviewItem[]) =>
      list.map((g) => {
        if (g.entity_name) return g;
        const hostId = hostEntityIdFromOverview(g);
        if (hostId == null) return g;
        const name = (findHostEntity(subjectTypeFromOverview(g), hostId) as { name?: string } | null)?.name;
        return name ? { ...g, entity_name: name } : g;
      }),
    [findHostEntity]
  );
  const grids = useMemo(() => withEntityNames(overviewGrids), [overviewGrids, withEntityNames]);
  const progressGrids = useMemo(() => withEntityNames(overviewProgressGrids), [overviewProgressGrids, withEntityNames]);
  // Right now can list rows the paged rail has not loaded yet — fall back to the progress set.
  const selectedGrid =
    (selectedId == null
      ? null
      : grids.find((g) => g.id === selectedId) ?? progressGrids.find((g) => g.id === selectedId)) ?? null;

  const { nameOf: userName, byUserId } = useBoardUsers();
  const activeGrids = useMemo(() => grids.filter((g) => !g.archived), [grids]);
  // The server already matched the rail's search text and repeats (list rows do not carry `recurrence`), so neither is applied again here.
  const visibleGrids = useMemo(
    () => filterGrids(activeGrids, { ...filters, query: "", recurrence: [] }, userName),
    [activeGrids, filters, userName]
  );
  const visibleProgressGrids = useMemo(
    () => filterGrids(progressGrids.filter((g) => !g.archived), { ...filters, query: searchQuery }, userName),
    [progressGrids, filters, searchQuery, userName]
  );
  const isFiltered = countActiveFilters(filters) > 0;

  const [today] = useState(() => startOfDay(new Date()));
  const createdAt = selectedGrid?.created_at;
  const firstDay = useMemo(() => (createdAt ? boardDay(createdAt, timeZone) : null), [createdAt, timeZone]);
  const shownDay = selectedGrid?.recurrence === "none" ? today : firstDay && day < firstDay ? firstDay : day;

  const { definition, matrix, error: detailError, refresh: refreshDetail } = useCheckgridDetail(
    boardId,
    selectedId,
    shownDay,
    timeZone,
    selectedGrid
  );

  // Keep left-rail counts in sync when the open checklist’s matrix updates.
  // Ignore stale matrix left over from the previously selected row (one-frame flash).
  useEffect(() => {
    if (selectedId == null || !matrix?.summary || matrix.checklist_id !== selectedId) return;
    patchSummary(selectedId, matrix.summary);
  }, [
    selectedId,
    matrix?.checklist_id,
    matrix?.summary.item_count,
    matrix?.summary.applicable,
    matrix?.summary.unresolved,
    matrix?.summary.subject_count,
    matrix?.summary.attention_needed,
    matrix?.summary.missed,
    matrix?.summary.last_activity,
    patchSummary
  ]);

  // Board-list rows may lack host entity / recurrence — backfill from detail once retrieve lands.
  useEffect(() => {
    if (selectedId == null || !definition || definition.id !== selectedId) return;
    patchOverview(selectedId, {
      name: definition.name,
      subject_type: definition.subject_type,
      recurrence: definition.recurrence,
      recurrence_weekdays: definition.recurrence_weekdays,
      archived: definition.archived,
      can_view_grant: definition.can_view_grant,
      can_edit_grant: definition.can_edit_grant,
      task_id: definition.task_id ?? null,
      client_id: definition.client_id ?? null,
      resource_id: definition.resource_id ?? null,
      assigned_boarduser_ids: definition.subjects
        .map((s) => s.subject_id)
        .filter((id) => id !== UNASSIGNED_SUBJECT_ID)
    });
  }, [
    selectedId,
    definition?.id,
    definition?.name,
    definition?.subject_type,
    definition?.recurrence,
    definition?.archived,
    definition?.can_edit_grant,
    definition?.task_id,
    definition?.client_id,
    definition?.resource_id,
    definition?.subjects,
    patchOverview
  ]);

  const dashboardOpen = entityManager.isOpen;
  const wasDashboardOpen = useRef(dashboardOpen);
  useEffect(() => {
    if (wasDashboardOpen.current && !dashboardOpen) {
      void project
        .sync()
        .catch(() => undefined)
        .then(() => Promise.all([refresh(), refreshDetail()]));
    }
    wasDashboardOpen.current = dashboardOpen;
  }, [dashboardOpen, project, refresh, refreshDetail]);

  const canEditSelected = Boolean(selectedGrid && user?.can(selectedGrid.can_edit_grant ?? definition?.can_edit_grant ?? GRANTS.grant_four));

  const openChecklistEditor = useCallback(
    (
      entity: unknown,
      options: {
        checklistId?: number | string;
        assignedBoardUserIds?: number[];
        canEditGrant?: number;
        canViewGrant?: number;
      }
    ) => {
      editorHandler.openEditor(
        EDITOR_KEYS.checklistEditor,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error editor handler accepts null record for popup editors
        null,
        {
          entity,
          checklistId: options.checklistId,
          assignedBoardUserIds: options.assignedBoardUserIds,
          canEditGrant: options.canEditGrant,
          canViewGrant: options.canViewGrant,
          // Matrix checks are per person — editor is for structure/settings only.
          structureOnly: true
        },
        { by: "recordType", useExisting: true }
      );
    },
    [editorHandler]
  );

  const resolveEntity = useCallback(
    (subjectType: CheckGridSubjectType, entityId: number) => {
      const entity = findHostEntity(subjectType, entityId);
      if (!entity) {
        toast.error(`${entityTypeForSubjectType(subjectType)} not found.`);
        return null;
      }
      return entity;
    },
    [findHostEntity]
  );

  const handleAction = useCallback(
    async (action: CheckgridMenuAction, grid: CheckGridOverviewItem, extras?: { file?: File }) => {
      if (!boardId) return;
      const subjectType = subjectTypeFromOverview(grid);
      const entityId = hostEntityIdFromOverview(grid);
      const entityType = entityTypeForSubjectType(subjectType) as ChecklistEntityPathType;
      const entityFk = entityId != null ? entityType : null;

      // Board-list rows may omit host ids; retrieve fills task/resource/client.
      const resolveHost = async (): Promise<{
        entityType: ChecklistEntityPathType;
        entityId: number;
      } | null> => {
        if (entityId != null) return { entityType, entityId };
        const detail = await fetchChecklistDetail({ boardId, checklistId: grid.id });
        const host = {
          task_id: detail.checklist.taskId != null ? Number(detail.checklist.taskId) : null,
          client_id: detail.checklist.clientId != null ? Number(detail.checklist.clientId) : null,
          resource_id: detail.checklist.resourceId != null ? Number(detail.checklist.resourceId) : null
        };
        const hostId = hostEntityIdFromOverview(host);
        if (hostId == null) return null;
        return {
          entityType: entityTypeForSubjectType(
            subjectTypeFromOverview({ ...host, subject_type: grid.subject_type })
          ) as ChecklistEntityPathType,
          entityId: hostId
        };
      };

      try {
        if (action === "openEntity") {
          const host = await resolveHost();
          if (host) entityManager.openEntityManager(host);
          return;
        }

        if (action === "edit") {
          // Structure editor loads by checklist id; host entity is optional (board-only checklists).
          const entity = entityId != null ? resolveEntity(subjectType, entityId) : null;
          if (entityId != null && !entity) return;
          openChecklistEditor(entity, {
            checklistId: grid.id,
            assignedBoardUserIds: grid.assigned_boarduser_ids,
            canEditGrant: grid.can_edit_grant,
            canViewGrant: grid.can_view_grant
          });
          return;
        }

        if (action === "archive") {
          const nextArchived = !grid.archived;
          const ok = await confirm({
            title: nextArchived ? "Archive checklist" : "Restore checklist",
            description: nextArchived
              ? `Are you sure you want to archive "${grid.name}"? It will be hidden from the active list.`
              : `Restore "${grid.name}" to the active list?`,
            confirmText: nextArchived ? "Archive" : "Restore",
            confirmButton: {
              variant: nextArchived ? "destructive" : "default"
            },
            cancelButton: {
              variant: "ghost"
            }
          });
          if (!ok) return;
          await patchChecklistMutation.mutateAsync({
            boardId,
            checklistId: grid.id,
            body: { archived: nextArchived },
            entityType: entityFk,
            entityId
          });
          toast.success(nextArchived ? "Checklist archived" : "Checklist restored");
          await refresh();
          if (nextArchived && selectedId === grid.id) setSelectedId(null);
          return;
        }

        if (action === "copyLink") {
          const host = await resolveHost();
          if (!host || !entityManager.copyEntityLink) {
            toast.error("Unable to copy link");
            return;
          }
          await entityManager.copyEntityLink(WidgetParamKeys.CHECKLIST.EDIT, grid.id, "Checklist link copied successfully!", undefined, host);
          return;
        }

        if (action === "duplicate") {
          const host = await resolveHost();
          if (!host) {
            toast.error("Unable to duplicate this checklist.");
            return;
          }
          const hostFk = host.entityType;
          await duplicateChecklistMutation.mutateAsync({
            boardId,
            sourceChecklistId: grid.id,
            createBody: {
              title: `Copy of ${grid.name}`,
              [hostFk]: host.entityId,
              archived: grid.archived,
              can_edit_grant: grid.can_edit_grant,
              can_view_grant: grid.can_view_grant,
              recurrence: grid.recurrence ?? "none"
            },
            duplicateBody: {
              [hostFk]: host.entityId,
              archived: grid.archived,
              canEdit: grid.can_edit_grant,
              canView: grid.can_view_grant,
              duplicate_id: grid.id
            },
            entityType: hostFk,
            entityId: host.entityId
          });
          toast.success("Checklist Duplicated", {
            description: `"${grid.name}" has been successfully duplicated`
          });
          await refresh();
          return;
        }

        if (action === "import") {
          const file = extras?.file;
          if (!file) return;
          if (file.type !== "text/plain") {
            toast.error("Unsupported File Type", { description: "Please upload files with extensions: .txt" });
            return;
          }
          const text = await file.text();
          await importChecklistItemsMutation.mutateAsync({
            boardId,
            checklistId: grid.id,
            checklistItemsData: text,
            entityType: entityFk,
            entityId
          });
          toast.success("Import Successful", { description: `Items imported successfully to "${grid.name}"` });
          await Promise.all([refresh(), selectedId === grid.id ? refreshDetail() : Promise.resolve()]);
          return;
        }

        if (action === "export") {
          await exportChecklistItemsMutation.mutateAsync({
            checklistId: grid.id,
            fileName: grid.name
          });
          toast.success("Export Successful", { description: `Items from "${grid.name}" exported successfully` });
          return;
        }

        if (action === "nudge") {
          setNudgeChecklist(checklistRecordFromOverview(grid));
          return;
        }

        if (action === "delete") {
          const ok = await confirm({
            title: `Delete checklist "${grid.name}"`,
            description: `Are you sure you want to delete the checklist "${grid.name}"?`,
            confirmText: "Delete",
            cancelButton: { variant: "ghost" },
            confirmButton: {
              className: "dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors"
            }
          });
          if (!ok) return;
          await deleteChecklistMutation.mutateAsync({
            boardId,
            checklistId: grid.id,
            entityType: entityFk,
            entityId
          });
          toast.success("Checklist Deleted", { description: `"${grid.name}" has been successfully deleted` });
          if (selectedId === grid.id) setSelectedId(null);
          await refresh();
        }
      } catch (err: unknown) {
        toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Action failed.");
      }
    },
    [
      boardId,
      confirm,
      deleteChecklistMutation,
      duplicateChecklistMutation,
      entityManager,
      exportChecklistItemsMutation,
      importChecklistItemsMutation,
      openChecklistEditor,
      patchChecklistMutation,
      refresh,
      refreshDetail,
      resolveEntity,
      selectedId
    ]
  );

  const handlePatchCell = async (checklistItemId: number, body: CheckGridCellPatch) => {
    if (!boardId || !selectedId) return;
    setSavingCell(true);
    try {
      await patchChecklistCellMutation.mutateAsync({
        boardId,
        checklistId: selectedId,
        checklistItemId,
        body
      });
      // Mutation invalidates matrix + detail; await refetch so UI settles before unlock.
      await refreshDetail();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Unable to update cell.");
      throw err;
    } finally {
      setSavingCell(false);
    }
  };

  const handleLoadHistory = async (checklistItemId: number, boardUserId?: number) => {
    if (!boardId || !selectedId) return [];
    const rows = await fetchChecklistItemStatusLogQuery({
      boardId,
      itemId: checklistItemId,
      boardUserId
    });
    return historyFromStatusLog(rows, (userId) => byUserId(userId)?.name);
  };

  if (!boardId) {
    return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Select a board to view Checklists.</div>;
  }

  return (
    <div className="flex h-full min-h-0 w-full bg-background">
      <CheckgridsRail
        grids={visibleGrids}
        selectedId={selectedId}
        onSelectOverview={() => setSelectedId(null)}
        onSelectGrid={setSelectedId}
        onAction={handleAction}
        isFiltered={isFiltered}
        isLoading={isLoading && !grids.length}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        filterBar={
          <CheckgridsFilters
            grids={activeGrids}
            filters={filters}
            onChange={setFilters}
            matchCount={visibleGrids.length}
            totalCount={totalCount}
            userName={userName}
          />
        }
      />

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {error && !grids.length ? (
          <LoadError message={error} onRetry={() => void refresh()} />
        ) : selectedId && !selectedGrid && (isLoading || isProgressLoading) ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading Checklists…</div>
        ) : selectedGrid ? (
          // Only render detail when matrix belongs to the selected checklist (avoids a flash of the previous grid).
          !matrix || definition?.id !== selectedGrid.id ? (
            detailError ? (
              <LoadError message={detailError} onRetry={() => void refreshDetail()} />
            ) : (
              <CheckgridDetailSkeleton name={selectedGrid.name} />
            )
          ) : (
            <CheckgridDetail
              key={selectedGrid.id}
              grid={selectedGrid}
              definition={definition}
              matrix={matrix}
              day={shownDay}
              firstDay={firstDay}
              onDayChange={setDay}
              onAction={handleAction}
              onPatchCell={handlePatchCell}
              onLoadHistory={handleLoadHistory}
              canEdit={canEditSelected}
              isSaving={savingCell}
            />
          )
        ) : progressError && !progressGrids.length ? (
          <LoadError message={progressError} onRetry={() => void refresh()} />
        ) : (
          <CheckgridsOverview
            grids={visibleProgressGrids}
            onSelect={setSelectedId}
            onAction={handleAction}
            isFiltered={isFiltered}
            isLoading={isProgressLoading && !progressGrids.length}
          />
        )}
      </div>

      {nudgeChecklist && (
        <NudgeUsersModal
          open
          onOpenChange={(open) => {
            if (!open) setNudgeChecklist(null);
          }}
          checklist={nudgeChecklist}
          skipProjectSync
          onUpdateChecklist={async (checklist, updates) => {
            // List rows may omit grants; never pass 0/undefined into user.can (throws).
            const editGrant = checklist.canEdit ?? GRANTS.grant_four;
            if (user && !user.can(editGrant)) {
              toast.error("Permission Denied", {
                description: "You don't have permission to edit this checklist"
              });
              return false;
            }
            Object.assign(checklist, updates);
            if (boardId && updates.assignedBoardUsers !== undefined) {
              try {
                await patchChecklistMutation.mutateAsync({
                  boardId,
                  checklistId: checklist.id,
                  body: { assigned_boardusers: updates.assignedBoardUsers }
                });
              } catch {
                toast.error("Unable to update assignees.");
                return false;
              }
            }
            return true;
          }}
        />
      )}
    </div>
  );
}
